<template>
  <v-select :label="hideLabel ? '' : label"
            :items="options"
            item-title="name"
            item-value="id"
            :readonly="readonly"
            :loading="loading" :disabled="disabled"
            :menu-icon="menuIcon"
            :multiple="multiple"
            :error-messages="errorMessage"
            :hint="hint" :persistent-hint="!!hint"
            :variant="outlined ? 'outlined' : undefined"
            :density="dense ? 'compact' : undefined"
            :model-value="value"
            @update:model-value="$emit('input', $event)"
            @blur="$emit('blur')">
    <!-- BOLINHA: só ela vai no slot, porque o nome já vem do `props`, que carrega o `title`
         resolvido pelo item-title. Escrevê-lo de novo mostra a opção duas vezes — foi o
         defeito que o campo de situação teve na migração.

         CHIP: aí o nome faz parte do desenho — é ele que leva o fundo —, então o `title` do
         `props` precisa sair, senão a opção aparece duas vezes pelo caminho oposto. -->
    <template #item="{item, props}">
      <v-list-item v-if="form === 'chip'" v-bind="{...props, title: undefined}"
                   :density="dense ? 'compact' : undefined" ripple>
        <ontos-colored-value :option="item" :form="form"/>
      </v-list-item>
      <v-list-item v-else v-bind="props" :density="dense ? 'compact' : undefined" ripple>
        <template #prepend>
          <v-icon size="small" :color="item.color">{{ VALUE_DOT }}</v-icon>
        </template>
      </v-list-item>
    </template>
    <!-- Um `span` em linha, e NÃO um v-list-item: o campo de situação usava list-item aqui
         e pagava duas vezes por isso. A altura mínima dele levantava o campo em relação aos
         vizinhos, e a regra `.v-list-item__prepend > .v-icon` do Vuetify aplica
         `opacity: var(--v-medium-emphasis-opacity)` — era o aspecto lavado, uma nuance que
         caía num campo e não no outro, sobre a mesma cor da paleta. -->
    <template #selection="{item, index}">
      <!-- O que JÁ está escolhido se marca igual ao da lista, e SEM o chip da biblioteca: ele
           vinha cinza, com moldura própria e um X pesado dentro — três coisas brigando com o
           desenho do valor, que já tem a cor dele. É o mesmo caminho que o campo de
           relacionamento fez, e o X discreto é o de lá.

           Sem o chip, o fecho deixa de vir do Vuetify; ele volta aqui como botão discreto, e só
           quando remover é permitido. -->
      <!-- Chip ou bolinha, pelo mesmo `ColoredValue` que desenha o valor no card e na ficha. A
           bolinha era escrita à mão aqui — ícone `small`, 32px de vão para alinhar com a lista e o
           nome no corpo do campo —, e o campo de situação ficava maior que os vizinhos (relato do
           usuário). -->
      <span v-if="item" :key="index" class="choice-selection">
        <ontos-colored-value :option="item" :form="form"/>
        <ontos-remove-button v-if="canRemove" @click.stop.prevent="remove(item)"/>
      </span>
    </template>
  </v-select>
</template>

<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import {isClearable, menuIconOf} from '@/utils/fieldRules'
import ColoredValue from '@/components/shared/ColoredValue.vue'
import RemoveButton from '@/components/shared/RemoveButton.vue'
import {ColoredOption, ValueForm, VALUE_DOT} from '@/types/ColoredOption'

/**
 * Seletor de opções fixas, cada uma com a sua bolinha colorida.
 *
 * É o campo do atributo de seleção E o campo de situação do fluxo — eram dois componentes
 * quase iguais, e a diferença que os separava deixou de existir quando a situação passou a
 * guardar a própria cor: os dois agora RECEBEM opções já coloridas e não calculam nada.
 * Enquanto foram dois, divergiram no visual — a bolinha de um colada no texto, a do outro
 * lavada e o campo mais alto que os vizinhos.
 *
 * Não conhece `IdeaProperty`: recebe rótulo, opções e cardinalidade. É o que permite ao
 * campo de situação usá-lo, já que situação não é propriedade da coleção.
 *
 * `item` nos slots já é a opção original, com a cor: no Vuetify 4 o item interno mudou para
 * `internalItem`, e o `item.raw` do Vuetify 3 viria undefined — a bolinha sem cor e o nome
 * vazio.
 */
@Component({name: 'ontos-choice-input', emits: ['input', 'blur'],
  components: {'ontos-colored-value': ColoredValue, 'ontos-remove-button': RemoveButton}})
class ChoiceInput extends Vue {
  //Exposto ao template: constante de módulo não é enxergada de lá.
  VALUE_DOT = VALUE_DOT

  /**
   * A forma dos valores — ver `ValueForm`. **Chip por padrão, porque o padrão é opção de
   * domínio**; o campo de situação pede `dot`.
   *
   * Entra por prop porque este campo serve às duas naturezas, e elas se desenham diferente em
   * todo o resto do app. Desenhar as duas com a bolinha fazia a opção de domínio aparecer no
   * formulário com a estética da situação — a mesma coisa que já acontecia no card do Kanban.
   */
  @Prop({type: String as () => ValueForm, default: 'chip'}) readonly form!: ValueForm

  @Prop({type: String, default: ''}) readonly label!: string
  /** Id da opção, ou lista de ids quando é múltiplo. */
  @Prop({default: null}) readonly value!: number | number[] | null
  @Prop({type: Array, default: () => []}) readonly options!: ColoredOption[]
  @Prop({type: Boolean, default: false}) readonly multiple!: boolean
  @Prop({type: Boolean, default: false}) readonly readonly!: boolean
  /** O progresso do próprio campo, e travado — a prévia vai mudar as opções (ver o `InstanceInput.affectedBy`). */
  @Prop({type: Boolean, default: false}) readonly loading!: boolean
  @Prop({type: Boolean, default: false}) readonly disabled!: boolean
  /** Obrigatório não se limpa: sem isto o X apagaria um valor que tem de existir. */
  @Prop({type: Boolean, default: false}) readonly required!: boolean
  @Prop({type: Boolean, default: false}) readonly hideLabel!: boolean
  @Prop({type: Boolean, default: false}) readonly outlined!: boolean
  @Prop({type: Boolean, default: false}) readonly dense!: boolean
  @Prop({type: String, default: ''}) readonly errorMessage!: string
  /** Orientação embaixo do campo, no lugar das mensagens do próprio Vuetify — o erro passa por cima. */
  @Prop({type: String, default: ''}) readonly hint!: string

  /**
   * O × junto do valor, também no de um valor só, e no lugar do X de limpar do Vuetify — que ficava
   * longe do valor e só aparecia com o mouse por cima (relato do usuário: o modelo é o da Parceria).
   * No simples vale a regra de quem limpa (`isClearable`): obrigatório não se limpa.
   */
  get canRemove(): boolean {
    return this.multiple ? !this.readonly : isClearable(this)
  }

  /** Tira um valor — da lista, ou o único. O mesmo gesto do campo de relacionamento, pelo mesmo motivo. */
  remove(item: any) {
    if (!this.multiple) {
      this.$emit('input', null)
      return
    }
    const id = Number(item && item.id !== undefined ? item.id : item)
    const kept = (Array.isArray(this.value) ? this.value : [])
        .filter((current: any) => Number(current) !== id)
    this.$emit('input', kept)
  }

  get menuIcon(): string | undefined {
    return menuIconOf(this.readonly)
  }
}

export default toNative(ChoiceInput)
</script>

<style scoped>
/* O mesmo arranjo do valor escolhido no campo de relacionamento: o valor e o X numa linha só,
   com folga à direita para os escolhidos não se emendarem quando quebram em duas linhas. */
.choice-selection {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  margin: 2px 10px 2px 0;
}
</style>
